// A project's documents rebuilt from the relay: each entry opened with the project key, snapshots loaded, batches
// applied — with the Apple apps' epoch rules (SuiteModel/DocumentStore+Epochs.swift; Web/core/src/epochs):
//
// - Each document keeps `synced`: the heads this device and the relay agree on (everything received, and this device's
//   pushes once acknowledged — `acknowledge`). Batches this device made but the relay hasn't acknowledged aren't in it.
// - A snapshot of a newer (epoch, lineage) is held as `pending` until the owner has let this device's batches for the
//   document settle (`settle`: anything that may have reached the relay acknowledged, so it's synced). Then it's
//   adopted: the old copy is archived (`archives`, with what of it was synced and what was carried over), the relay's
//   copy of the old history absorbed as the Swift absorbs an older epoch (recording `absorbedHeads`), this device's
//   unsynced edits rebased, comments re-anchored — an `adopted` event with the changes for the outbox. Batches of the
//   newer epoch that arrive meanwhile go to the pending copy.
// - A batch (or snapshot) of an older epoch is absorbed as the Swift's `absorbOlderFragmentEpoch` absorbs a document of
//   that epoch: the one given is the relay's copy of that history (the archived copy as synced, plus the batch), with the
//   archived copy for the Swift's third case; then the batch joins the archive (an `absorbed` event). When the Swift would
//   throw `differentEpoch` (no record relates them, no copy of that epoch here), it's kept unapplied and reported
//   (`unplaced`): whoever holds that history absorbs it.
// - While a cache is replayed (`deferEpochWork`), adoption happens without settling (everything replayed is synced) and
//   absorption waits for `settleEpochs`, so changes already in the cache aren't made again.
import * as A from '@automerge/automerge';
import {
  absorbOlderEpoch, adoptEpoch, changeHashes, copyAt, DifferentEpoch, epochIDOf, rebaseOnto, remapCheckpoints, splitChanges,
  type AdoptionOutcome,
} from '@writing-suite/core';
import { open } from '../shared/crypto.ts';
import { outranks, type WireEntry } from '../shared/wire.ts';

/** What `load` needs of a connection and its welcome (a `SyncConnection` and its `Loaded`; structural, so a host with
 *  no WebSocket — the relay's agents — can use the replica too). */
interface PackSource {
  pack(keys: string[]): Promise<Map<string, Uint8Array>>;
}
interface LoadedEntries {
  entries: WireEntry[];
  inline: Map<number, Uint8Array>;
  head: number;
}

type Doc = A.Doc<Record<string, unknown>>;

export interface ReplicaDoc {
  doc: Doc;
  epoch: number;
  lineage: string;
  /** Heads this device and the relay agree on (received entries, acknowledged pushes). */
  synced: string[];
}

/** An older history's copy, kept after a newer epoch was adopted (for absorbing what still arrives in it). */
export interface ArchivedDoc extends ReplicaDoc {
  /** Heads whose content was rebased into the newer epoch here: never carried over again. */
  rebased: string[];
}

export type EpochEvent =
  | {
      type: 'adopted'; doc: string; outcome: AdoptionOutcome; from: { epoch: number; lineage: string }; to: { epoch: number; lineage: string };
      /** The document's heads before (another history's: the editor takes the new text whole), and right after. */
      beforeHeads: A.Heads;
      heads: A.Heads;
      /** Deterministic absorbing changes, then this device's own (rebase, re-anchoring): all to send, new epoch. */
      changes: Uint8Array[];
      hashes: string[];
      /** Other documents this changed (the project's checkpoints remapped). */
      touched: string[];
    }
  /** Edits of an older epoch merged in, as this device's own change (batched as usual); `touched`: the project's
   *  checkpoints remapped. */
  | { type: 'absorbed'; doc: string; beforeHeads: A.Heads; touched: string[] }
  | { type: 'rebased'; doc: string; beforeHeads: A.Heads; heads: A.Heads; outcome: AdoptionOutcome; changes: Uint8Array[]; hashes: string[] }
  | { type: 'unplaced'; doc: string; epoch: number; lineage: string; seq: number; own: boolean; reason: string };

export interface ReplicaOptions {
  /** Before a newer epoch replaces `doc`: resolve true once this device's batches for it that may be on the relay are
   *  acknowledged; false to wait (the owner calls `adoptPending` later). */
  settle?: (doc: string) => Promise<boolean>;
  /** The adoption didn't happen after a successful `settle` (let the document's batches go again). */
  release?: (doc: string) => void;
  /** An archive changed (to keep it). */
  onArchive?: (doc: string, archive: ArchivedDoc) => void;
}

const same = (a: { epoch: number; lineage: string }, b: { epoch: number; lineage: string }) => a.epoch === b.epoch && a.lineage === b.lineage;

/** `synced` with `hashes` added (those the document holds), direct dependencies of the new ones dropped. */
function addSynced(target: ReplicaDoc, hashes: readonly string[]) {
  if (!hashes.length) return;
  const b = A.getBackend(target.doc);
  const set = new Set(target.synced);
  for (const h of hashes) {
    const meta = b.getChangeMetaByHash(h);
    if (!meta) continue;
    set.add(h);
    for (const d of meta.deps) set.delete(d);
  }
  target.synced = [...set].sort();
}

export class Replica {
  readonly docs = new Map<string, ReplicaDoc>();
  /** Older histories' copies, by document, oldest first. */
  readonly archives = new Map<string, ArchivedDoc[]>();
  /** Newer epochs received, waiting to be adopted. */
  readonly pending = new Map<string, ReplicaDoc>();
  cursor = 0;
  /** Entries not applied (another key's, damaged, a batch before its snapshot, an older history with no way in). */
  setAside = 0;
  /** What happened to epochs since `takeEvents` was last called. */
  private events: EpochEvent[] = [];
  /** Replaying a cache: adopt without settling, absorb later (`settleEpochs`). */
  deferEpochWork = false;
  private readonly dirtyArchives = new Set<string>();
  private readonly adopting = new Map<string, Promise<void>>();

  /** Keys the project had before its current one (W9a rotation): an entry sealed before the key changed still opens. */
  previousKeys: Uint8Array[] = [];

  constructor(
    /** The project's key now (replaced when it rotates). */
    public key: Uint8Array,
    readonly options: ReplicaOptions = {},
  ) {}

  /** An entry's bytes, opened with the current key or (sealed before a rotation) an earlier one. */
  async openEntry(sealed: Uint8Array): Promise<Uint8Array> {
    try {
      return await open(this.key, sealed);
    } catch (e) {
      for (const k of this.previousKeys) {
        try {
          return await open(k, sealed);
        } catch {
          // not this one
        }
      }
      throw e;
    }
  }

  takeEvents(): EpochEvent[] {
    const e = this.events;
    this.events = [];
    return e;
  }

  archive(name: string, e: { epoch: number; lineage: string }): ArchivedDoc | undefined {
    return this.archives.get(name)?.find((a) => same(a, e));
  }

  /** Keeps an archived copy (restored from the offline store, or made by an adoption), merging with one of its epoch. */
  putArchive(name: string, archive: ArchivedDoc): ArchivedDoc {
    const list = this.archives.get(name) ?? [];
    const had = list.find((a) => same(a, archive));
    if (had) {
      had.doc = A.merge(had.doc, archive.doc);
      addSynced(had, archive.synced);
      had.rebased = [...new Set([...had.rebased, ...archive.rebased])].sort();
      return had;
    }
    list.push(archive);
    list.sort((a, b) => (outranks(a.epoch, a.lineage, b.epoch, b.lineage) ? 1 : -1));
    this.archives.set(name, list);
    return archive;
  }

  /**
   * Applies a sealed entry. `own`: one of this device's batches the relay hasn't acknowledged (kept from an earlier
   * session) — applied, but not synced.
   */
  async apply(entry: WireEntry, sealed: Uint8Array, o: { own?: boolean } = {}): Promise<void> {
    const name = entry.doc;
    if (entry.seq > 0) this.cursor = Math.max(this.cursor, entry.seq);
    // Attachments (`attachment.<hmac>`, PROTOCOL.md §2.3) are files, not documents: the Apple apps store them; the web
    // reads none yet, so they pass by (the cursor moves on).
    if (name.startsWith('attachment.')) return;
    const bytes = await this.openEntry(sealed);
    const e = { epoch: entry.epoch, lineage: entry.lineage };
    const synced = !o.own;
    const have = this.docs.get(name);
    if (entry.kind === 'snapshot') {
      const loaded = A.load<Record<string, unknown>>(bytes, have ? { actor: A.getActorId(have.doc) } : undefined);
      if (!have) {
        this.docs.set(name, { doc: loaded, ...e, synced: synced ? [...A.getHeads(loaded)].sort() : [] });
      } else if (same(have, e)) {
        const heads = A.getHeads(loaded);
        have.doc = A.merge(have.doc, loaded);
        if (synced) addSynced(have, heads);
      } else if (outranks(e.epoch, e.lineage, have.epoch, have.lineage)) {
        const p = this.pending.get(name);
        if (p && same(p, e)) {
          const heads = A.getHeads(loaded);
          p.doc = A.merge(p.doc, loaded);
          addSynced(p, heads);
        } else if (!p || outranks(e.epoch, e.lineage, p.epoch, p.lineage)) {
          this.pending.set(name, { doc: loaded, ...e, synced: [...A.getHeads(loaded)].sort() });
        } else {
          return this.unplace(entry, !synced, 'a version between this copy and the one waiting to be adopted');
        }
        await this.adoptWhenSettled(name);
      } else {
        const archive = this.archive(name, e);
        if (!archive) return this.unplace(entry, !synced, 'no copy of that version here');
        const heads = A.getHeads(loaded);
        const remote = synced && !this.deferEpochWork ? A.merge(this.relayCopy(archive), loaded) : null;
        const placed = remote ? this.absorbFrom(name, remote, archive.doc) : true;
        archive.doc = A.merge(archive.doc, loaded);
        if (synced) addSynced(archive, heads);
        this.archiveChanged(name, archive, !synced);
        if (!placed) this.unplace(entry, false, 'its version can’t be related to this one');
      }
      return;
    }
    if (!have) return this.unplace(entry, !synced, 'a batch before its snapshot');
    if (same(have, e)) return void this.applyBatch(have, bytes, synced);
    const p = this.pending.get(name);
    if (p && same(p, e)) return void this.applyBatch(p, bytes, true);
    if (outranks(e.epoch, e.lineage, have.epoch, have.lineage)) return this.unplace(entry, !synced, 'a newer version’s batch before its snapshot');
    const archive = this.archive(name, e);
    if (!archive) return this.unplace(entry, !synced, 'no copy of that version here');
    // The document the Swift would be given: the relay's copy of that history (the archive as synced) with the batch.
    const remote = synced && !this.deferEpochWork ? this.relayCopy(archive, bytes) : null;
    const hashes = changeHashes(bytes);
    if (remote && hashes.length && !hashes.every((h) => A.getBackend(remote).getChangeMetaByHash(h))) {
      return this.unplace(entry, false, 'history this copy doesn’t have');
    }
    const placed = remote ? this.absorbFrom(name, remote, archive.doc) : true;
    this.applyBatch(archive, bytes, synced);
    this.archiveChanged(name, archive, !synced);
    if (!placed) this.unplace(entry, false, 'its version can’t be related to this one');
  }

  /** The relay's copy of an archived history (as synced) with `batch` applied. */
  private relayCopy(archive: ArchivedDoc, batch?: Uint8Array): A.Doc<unknown> {
    // Nothing synced: none of that history is the relay's (the batch then has nothing to apply to).
    const copy = archive.synced.length ? copyAt(archive.doc, archive.synced) : A.init<unknown>();
    const changes = batch ? splitChanges(batch) : null;
    if (changes) return A.applyChanges(copy, changes)[0];
    return batch ? A.loadIncremental(copy, batch) : copy;
  }

  /**
   * `absorbOlderFragmentEpoch` (or `…ProjectEpoch`) of `remote` into the current document, with `archived` for the
   * Swift's third case; the project's checkpoints remapped after a fragment's. False when the Swift would throw
   * `differentEpoch`.
   */
  private absorbFrom(name: string, remote: A.Doc<unknown>, archived: A.Doc<unknown> | null): boolean {
    const current = this.docs.get(name);
    if (!current) return true;
    const beforeHeads = A.getHeads(current.doc);
    const fragment = name.startsWith('fragment.');
    let r;
    try {
      r = absorbOlderEpoch(current.doc, remote, archived, { fragment, project: name === 'project' });
    } catch (e) {
      if (e instanceof DifferentEpoch) return false;
      throw e;
    }
    if (!r.changed) return true;
    current.doc = r.doc as Doc;
    const touched: string[] = [];
    const project = fragment ? this.docs.get('project') : undefined;
    if (project) {
      const remapped = remapCheckpoints(project.doc, name.slice('fragment.'.length), remote, current.doc, [[...A.getHeads(current.doc)]],
        'Remapped checkpoints after adopting a compacted history');
      if (remapped !== project.doc) {
        project.doc = remapped as Doc;
        touched.push('project');
      }
    }
    this.events.push({ type: 'absorbed', doc: name, beforeHeads, touched });
    return true;
  }

  private applyBatch(target: ReplicaDoc, bytes: Uint8Array, synced: boolean): string[] {
    target.doc = A.loadIncremental(target.doc, bytes);
    const hashes = changeHashes(bytes);
    if (synced) addSynced(target, hashes);
    return hashes;
  }

  private unplace(entry: WireEntry, own: boolean, reason: string) {
    this.setAside++;
    this.events.push({ type: 'unplaced', doc: entry.doc, epoch: entry.epoch, lineage: entry.lineage, seq: entry.seq, own, reason });
  }

  private archiveChanged(name: string, archive: ArchivedDoc, own: boolean) {
    this.options.onArchive?.(name, archive);
    // This device's own old-epoch edits wait for `rebaseArchived` (after they've settled); a replayed cache's, for
    // `settleEpochs`.
    if (!own && this.deferEpochWork) this.dirtyArchives.add(name);
  }

  /** This device's own batch the relay acknowledged: its changes are synced in whichever copy they belong to. */
  acknowledge(doc: string, e: { epoch: number; lineage: string }, hashes: readonly string[]) {
    const target = [this.docs.get(doc), this.pending.get(doc), this.archive(doc, e)].find((t) => t && same(t, e));
    if (target) addSynced(target, hashes);
  }

  // MARK: Adoption

  private adoptWhenSettled(name: string): Promise<void> {
    if (this.deferEpochWork) {
      this.adoptNow(name, false);
      return Promise.resolve();
    }
    const running = this.adopting.get(name);
    if (running) return running;
    const run = (async () => {
      try {
        if (this.options.settle && !(await this.options.settle(name))) return; // waits: `adoptPending` later
        if (!this.adoptNow(name, true)) this.options.release?.(name);
      } catch (e) {
        this.options.release?.(name);
        throw e;
      } finally {
        this.adopting.delete(name);
      }
    })();
    this.adopting.set(name, run);
    return run;
  }

  /** Adopts every newer epoch still waiting (after the outbox reconnects). */
  async adoptPending(): Promise<void> {
    for (const name of [...this.pending.keys()]) await this.adoptWhenSettled(name);
  }

  /** Replaces the document with its pending newer epoch now. False when there was nothing to adopt. */
  private adoptNow(name: string, live: boolean): boolean {
    const have = this.docs.get(name), p = this.pending.get(name);
    if (!have || !p) return false;
    this.pending.delete(name);
    const beforeHeads = A.getHeads(have.doc);
    const received = [...A.getHeads(p.doc)];
    const fragment = name.startsWith('fragment.');
    if (!live) {
      // Replaying a cache: everything here is synced, so the new epoch is taken as it is; what it absorbs is worked out
      // once the whole cache is in (`settleEpochs`), so changes already cached aren't made again.
      const archive = this.putArchive(name, { doc: have.doc, epoch: have.epoch, lineage: have.lineage, synced: have.synced, rebased: [...beforeHeads].sort() });
      this.docs.set(name, { ...p });
      this.options.onArchive?.(name, archive);
      this.dirtyArchives.add(name);
      return true;
    }
    const adoption = adoptEpoch({ doc: have.doc, synced: have.synced }, p.doc, { fragment, project: name === 'project', actor: A.getActorId(have.doc) });
    const archive = this.putArchive(name, { doc: have.doc, epoch: have.epoch, lineage: have.lineage, synced: have.synced, rebased: adoption.archive.rebased ?? [] });
    const next: ReplicaDoc = { doc: adoption.doc as Doc, epoch: p.epoch, lineage: p.lineage, synced: p.synced };
    this.docs.set(name, next);
    this.options.onArchive?.(name, archive);
    const touched: string[] = [];
    if (fragment) {
      // Checkpoints that name the old history's states point at the same states in the new one.
      const project = this.docs.get('project');
      if (project) {
        const remapped = remapCheckpoints(project.doc, name.slice('fragment.'.length), have.doc, next.doc, [received, [...A.getHeads(next.doc)]]);
        if (remapped !== project.doc) {
          project.doc = remapped as Doc;
          touched.push('project');
        }
      }
    }
    const changes = adoption.own;
    this.events.push({
      type: 'adopted', doc: name, outcome: adoption.outcome, from: { epoch: have.epoch, lineage: have.lineage }, to: { epoch: p.epoch, lineage: p.lineage },
      beforeHeads, heads: [...A.getHeads(next.doc)], changes, hashes: changes.map((c) => A.decodeChange(c).hash), touched,
    });
    return true;
  }

  // MARK: Absorbing

  /** Absorbs the relay's copy of the newest archived history of `name` (everything synced of it), as the Swift absorbs
   *  an older epoch: nothing when it was taken in already (`absorbedHeads`). */
  private absorbArchived(name: string) {
    const list = this.archives.get(name);
    const archive = list?.[list.length - 1];
    if (archive?.synced.length) this.absorbFrom(name, this.relayCopy(archive), archive.doc);
  }

  /** After a cache was replayed: the absorbing it deferred. */
  settleEpochs(): EpochEvent[] {
    this.deferEpochWork = false;
    for (const name of [...this.dirtyArchives]) {
      this.dirtyArchives.delete(name);
      this.absorbArchived(name);
    }
    return this.takeEvents();
  }

  /** Documents whose archived copy holds edits of this device's never carried into the current epoch. */
  needsRebase(): string[] {
    const out: string[] = [];
    for (const [name, list] of this.archives) {
      const current = this.docs.get(name);
      const last = list[list.length - 1];
      if (!current || !last) continue;
      const known = [...last.synced, ...last.rebased].filter((h) => A.getBackend(last.doc).getChangeMetaByHash(h));
      const beyond = A.getBackend(last.doc).getChangesMeta(known);
      if (beyond.length) out.push(name);
    }
    return out;
  }

  /**
   * Rebases the newest archive's unsynced edits of `name` (this device's own batches of the old epoch, found pending
   * after a reload, once settled) into the current document: a `rebased` event with the changes to send.
   */
  rebaseArchived(name: string): EpochEvent | null {
    const current = this.docs.get(name);
    const list = this.archives.get(name);
    const archive = list?.[list.length - 1];
    if (!current || !archive) return null;
    this.absorbArchived(name);
    const beforeHeads = A.getHeads(current.doc);
    const r = rebaseOnto({ doc: archive.doc, synced: archive.synced, rebased: archive.rebased }, current.doc, A.getActorId(current.doc));
    archive.rebased = [...A.getHeads(archive.doc)].sort();
    this.options.onArchive?.(name, archive);
    if (r.change) current.doc = r.doc as Doc;
    const event: EpochEvent = {
      type: 'rebased', doc: name, beforeHeads, heads: [...A.getHeads(current.doc)], outcome: r.outcome,
      changes: r.change ? [r.change] : [], hashes: r.change ? [A.decodeChange(r.change).hash] : [],
    };
    this.events.push(event);
    return event;
  }

  // MARK: Loading and reading

  /** Applies a welcome's entries: fetched blobs first, then snapshots before batches, each in order. */
  async load(conn: PackSource, loaded: LoadedEntries): Promise<void> {
    const blobs = await conn.pack(loaded.entries.flatMap((e) => (e.blob ? [e.blob] : [])));
    const bytes = (e: WireEntry) => (e.blob ? blobs.get(e.blob) : loaded.inline.get(e.seq));
    const ordered = [...loaded.entries.filter((e) => e.kind === 'snapshot'), ...loaded.entries.filter((e) => e.kind === 'batch')];
    const defer = this.deferEpochWork;
    this.deferEpochWork = true;
    try {
      for (const e of ordered) {
        const b = bytes(e);
        if (b) await this.apply(e, b).catch(() => void this.setAside++);
      }
    } finally {
      this.deferEpochWork = defer;
    }
    this.settleEpochs();
    this.cursor = Math.max(this.cursor, loaded.head);
  }

  text(docName: string): string {
    const d = this.docs.get(docName)?.doc as { text?: unknown; title?: unknown } | undefined;
    return typeof d?.text === 'string' ? d.text : '';
  }

  title(docName: string): string {
    const d = this.docs.get(docName)?.doc as { title?: unknown } | undefined;
    // Titles are scalar strings in the Swift's documents: Automerge 3 loads them as ImmutableString.
    const title = d?.title instanceof A.ImmutableString ? d.title.val : d?.title;
    return typeof title === 'string' ? title : docName;
  }

  words(docName?: string): number {
    const count = (t: string) => t.split(/\s+/).filter(Boolean).length;
    if (docName) return count(this.text(docName));
    let n = 0;
    for (const name of this.docs.keys()) if (name.startsWith('fragment.')) n += count(this.text(name));
    return n;
  }

  /** The epoch and lineage of a document here (the current copy). */
  epochOf(docName: string) {
    const d = this.docs.get(docName);
    return d ? epochIDOf(d.doc) : null;
  }
}
